import { useEffect, useRef, useState } from 'react';
import { IconClockHour4, IconTrophy } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useDigits } from './contestUi';

// Right side of the contest hero. Counts down to whatever happens next:
//   upcoming → the start · live → the end · pending → "results being processed" · completed → "results out".
// Times are the contest's real instants (UTC ISO), so every viewer sees the same remaining time.
// When it reaches zero the server phase is about to flip (live → pending), so `onElapsed` asks the page
// to refetch (retried every 10 s while the phase is still unchanged, in case the device clock is slightly ahead).

const RETRY_MS = 10 * 1000;

export default function ContestCountdown({ contest, phase, onElapsed }) {
    const { t } = useTranslation();
    const digits = useDigits();
    const pad = (n) => digits(String(n).padStart(2, '0'));

    const [now, setNow] = useState(() => Date.now());
    const lastFire = useRef(0);

    const start = contest?.startDate ? new Date(contest.startDate).getTime() : null;
    const end = contest?.endDate ? new Date(contest.endDate).getTime() : null;
    const target = phase === 'upcoming' ? start : phase === 'live' ? end : null;
    const label = phase === 'upcoming' ? t('contest.page.countdown.startsIn') : t('contest.page.countdown.endsIn');

    useEffect(() => {
        if (!target) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [target]);

    const left = target ? Math.max(0, target - now) : 0;
    useEffect(() => {
        if (!target || left > 0 || !onElapsed) return undefined;
        if (now - lastFire.current < RETRY_MS) return undefined;
        lastFire.current = now;
        const id = setTimeout(onElapsed, 1500);
        return () => clearTimeout(id);
    }, [target, left, now, onElapsed]);

    // Time is over: no clock, just where the contest stands.
    if (phase === 'pending' || phase === 'completed') {
        const Icon = phase === 'pending' ? IconClockHour4 : IconTrophy;
        return (
            <div className="flex max-w-[340px] items-start gap-3 rounded-[14px] border border-white/10 bg-white/[0.07] p-4">
                <Icon size={22} className="mt-0.5 flex-shrink-0 text-primary" />
                <div className="flex flex-col gap-0.5">
                    <span className="text-base font-semibold">{t(phase === 'pending' ? 'contest.page.countdown.pending' : 'contest.page.countdown.completed')}</span>
                    <span className="text-[13px] leading-snug text-zinc-300">{t(phase === 'pending' ? 'contest.leaderboard.pendingMessage' : 'contest.hero.completedNote')}</span>
                </div>
            </div>
        );
    }
    if (!target) return null;

    const sec = Math.floor(left / 1000);
    const tiles = [
        { v: Math.floor(sec / 86400), u: t('contest.page.countdown.days') },
        { v: Math.floor((sec % 86400) / 3600), u: t('contest.page.countdown.hours') },
        { v: Math.floor((sec % 3600) / 60), u: t('contest.page.countdown.minutes') },
        { v: sec % 60, u: t('contest.page.countdown.seconds') },
    ];

    return (
        <div className="flex flex-col gap-2.5">
            <div className="text-sm text-zinc-400">{label}</div>
            <div role="timer" aria-live="off" className="grid grid-cols-[repeat(4,minmax(64px,82px))] gap-2.5">
                {tiles.map((tile) => (
                    <div key={tile.u} className="rounded-[14px] border border-white/10 bg-white/[0.07] px-1.5 pb-2 pt-2.5 text-center">
                        <div className="font-baloo text-[32px] font-bold leading-[1.1] tabular-nums">{pad(tile.v)}</div>
                        <div className="text-[13px] text-zinc-400">{tile.u}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
